import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { useRef } from 'react';

import FallbackUI from '@/components/FallbackUI';
import { useBugReportToSlackMutation } from '@/api/slack-webhook';

const ErrorBoundary = ({ children }) => {
	const errorInfoRef = useRef(null);
	const errorRef = useRef(null);
	const [bugReportToSlack] = useBugReportToSlackMutation();

	const handleError = (error, errorInfo) => {
		// Store both error and errorInfo for later use
		errorRef.current = error;
		errorInfoRef.current = errorInfo;

		// eslint-disable-next-line no-console
		console.error('Error caught by boundary:', error, errorInfo);
	};

	// Send error report to Slack (only when user clicks button)
	const sendErrorToSlack = (error) => {
		// Get logged-in user information
		let userInfo = null;
		try {
			const userInfoStr = localStorage.getItem('userInfo');
			if (userInfoStr) {
				userInfo = JSON.parse(userInfoStr);
			}
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error('Failed to parse userInfo:', e);
		}

		// error message for Slack with blocks
		const errorMessage = {
			text: `Error Report from Admin Panel`,
			blocks: [
				{
					type: 'header',
					text: {
						type: 'plain_text',
						text: '🔴 Error Report - Admin Panel'
					}
				},
				{
					type: 'section',
					text: {
						type: 'mrkdwn',
						text: `<@U089K5P078T> Please check this issue!`
					}
				},
				{
					type: 'section',
					fields: [
						{
							type: 'mrkdwn',
							text: `*User:*\n${userInfo?.name || 'Unknown'}`
						},
						{
							type: 'mrkdwn',
							text: `*Email:*\n${userInfo?.email || 'Unknown'}`
						}
					]
				},
				{
					type: 'section',
					fields: [
						{
							type: 'mrkdwn',
							text: `*Department:*\n${userInfo?.designation || 'N/A'}`
						}
					]
				},
				{
					type: 'section',
					fields: [
						{
							type: 'mrkdwn',
							text: `*Error:*\n${error.message}`
						}
					]
				},
				{
					type: 'section',
					text: {
						type: 'mrkdwn',
						text: `*Stack Trace:*\n\`\`\`${error.stack?.substring(0, 2500) + 'End' || 'N/A'}\`\`\``
					}
				},
				{
					type: 'section',
					text: {
						type: 'mrkdwn',
						text: `*URL:*\n${window.location.href}`
					}
				}
			]
		};

		bugReportToSlack({ body: errorMessage });

		// eslint-disable-next-line no-console
		console.log('Bug report sent to Slack');
	};

	// Handler for manual submission from FallbackUI (when user clicks button)
	const handleManualSubmit = () => {
		if (errorRef.current) {
			sendErrorToSlack(errorRef.current);
		}
	};

	const handleReset = () => {
		errorRef.current = null;
		errorInfoRef.current = null;
	};

	return (
		<ReactErrorBoundary
			FallbackComponent={(props) => (
				<FallbackUI {...props} errorInfo={errorInfoRef.current} onSubmitIssue={handleManualSubmit} />
			)}
			onError={handleError}
			onReset={handleReset}
		>
			{children}
		</ReactErrorBoundary>
	);
};

export default ErrorBoundary;
